import React, { useEffect, useState } from 'react'
import axios from 'axios';
import { baseURL } from "../../../../Services/api/baseURL";
import Select from "react-select";
import { useNavigate, useParams } from 'react-router';
import Loader from '../../../Loader/Loader';
import VariantSalesChart from './VariantSalesChart';
import { ArrowLeftCircle } from 'react-feather';
import Pagination from '../../../CustomPagination/Pagination';

const VariantSalesHistory = () => {
  const { id } = useParams();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showChart, setShowChart] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const navigate = useNavigate()

  const [filters, setFilters] = useState({
    product: [],
    category: '',
    subcategory: '',
    numberOfProducts: '',
    start_date: "",
    end_date: ""
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
  };

  const uniqueDates = () => {
    const dates = inventory.map(record => formatDate(record.updatedAt));
    return [...new Set(dates)].sort();
  };

  const uniqueValues = (key) => [...new Set(inventory.map(record => record[key]))].sort();

  const getUniqueVariantNames = () => {
    return [...new Set(inventory.map(record => record?.variantName))]
      .filter(Boolean)
      .sort();
  };

  const VariantMultiSelect = ({ filters, setFilters, getUniqueVariantNames }) => {
    return (
      <Select
        isMulti
        options={getUniqueVariantNames().map((name) => ({
          value: name,
          label: name,
        }))}
        value={filters.product.map((p) => ({ value: p, label: p }))}
        onChange={(selected) =>
          setFilters({
            ...filters,
            product: selected.map((s) => s.value),
          })
        }
        placeholder="Select Variants"
        className="w-full py-2 px-2"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          placeholder: (base) => ({
            ...base,
            marginLeft: "10px",
            color: "#666",
          }),
          control: (base) => ({
            ...base,
            minHeight: "38px",
            height: "38px",
          }),
          valueContainer: (base) => ({
            ...base,
            height: "38px",
          }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
          }),
        }}
      />
    );
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
    setPage(1);
  };

  const filteredInventory = inventory.filter(record => {
    return (
      (filters.product.length === 0 || filters.product.includes(record?.variantName)) &&
      (filters.category === '' || record.category_name === filters.category) &&
      (filters.subcategory === '' || record.subcategory_name === filters.subcategory) &&
      (filters.numberOfProducts === '' || record.totalSold.toString() === filters.numberOfProducts)
    );
  });

  const totalPages = Math.ceil(filteredInventory.length / limit);

  const paginatedInventory = filteredInventory.slice(
    (page - 1) * limit,
    page * limit
  );

  // ── Export to CSV ──────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const headers = [
      "Variant Name",
      "Category",
      "Subcategory",
      // "Purchase Price",
      "Selling Price",
      "Variants Sold",
    ];

    const rows = filteredInventory.map((record) => [
      record?.variantName || "NA",
      record.category_name || "NA",
      record.subcategory_name || "NA",
      // record?.purchasePrice ?? "NA",
      record?.sellingPrice ?? "NA",
      record.totalSold ?? 0,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const dateStr = new Date().toISOString().split("T")[0];
    link.download = `variant_sales_history_${dateStr}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token"));
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await axios.get(
        `${baseURL}/api/order/get-sold-variant-history/${id}`,
        { headers: { Authorization: token }, params }
      );
      setInventory(response.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    fetchData();
  }, [id]);

  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      fetchData();
    }
    if (!filters.start_date && !filters.end_date) {
      fetchData();
    }
  }, [filters.start_date, filters.end_date]);

  const handleReset = () => {
    setFilters(prev => ({ ...prev, start_date: "", end_date: "" }));
    fetchData();
  };

  const toggleChart = () => {
    setShowChart(!showChart);
  };

  return (
    <div className="p-4">
      <button onClick={() => navigate(-1)} className="">
        <ArrowLeftCircle className="text-[#d3178a]" />
      </button>

      <div className='flex justify-between'>
        <h4 className='text-2xl font-bold mb-4'>Sold Variants Data for store</h4>

        {!showChart && (
          <div className="flex items-end gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium">Start Date</label>
              <input
                type="date"
                name="start_date"
                value={filters.start_date}
                onChange={handleFilterChange}
                className="border p-2 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">End Date</label>
              <input
                type="date"
                name="end_date"
                value={filters.end_date}
                onChange={handleFilterChange}
                className="border p-2 rounded"
              />
            </div>
            <button
              onClick={handleReset}
              className='rounded-2 bg-light-primary px-4 py-2.5 text-primary'
            >
              Reset
            </button>
          </div>
        )}

        <div className='flex items-center gap-2'>
          {/* ── Export Button ── */}
          {!showChart && (
            <button
              onClick={exportToCSV}
              disabled={filteredInventory.length === 0}
              className="flex items-center gap-1 bg-[#d3178a] hover:bg-[#b5136f] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded font-medium transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4"
                />
              </svg>
              Export
            </button>
          )}

          <button
            onClick={toggleChart}
            className={`px-4 py-2.5 rounded font-medium transition-colors ${
              showChart
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-[#d3178a] text-white hover:bg-[#de379d]'
            }`}
          >
            {showChart ? 'Hide Chart' : 'Sales Comparison Chart'}
          </button>
        </div>
      </div>

      {/* Conditional Chart Rendering */}
      {showChart && (
        <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h5 className="text-lg font-semibold">Sales Comparison Chart</h5>
            <button
              onClick={() => setShowChart(false)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>
          </div>
          <VariantSalesChart storeId={id} />
        </div>
      )}

      {!loading ? (
        inventory.length > 0 ? (
          <>
            <div className="overflow-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr>
                    <VariantMultiSelect
                      filters={filters}
                      setFilters={setFilters}
                      getUniqueVariantNames={getUniqueVariantNames}
                    />

                    <th className="px-2 py-2 border-b">
                      <select
                        name="category"
                        value={filters.category}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Categories</option>
                        {uniqueValues('category_name').map((value, index) => (
                          <option key={index} value={value}>{value}</option>
                        ))}
                      </select>
                    </th>

                    <th className="px-2 py-2 border-b">
                      <select
                        name="subcategory"
                        value={filters.subcategory}
                        onChange={handleFilterChange}
                        className="w-full"
                      >
                        <option value="">Subcategories</option>
                        {uniqueValues('subcategory_name').map((value, index) => (
                          <option key={index} value={value}>{value}</option>
                        ))}
                      </select>
                    </th>

                    <th className="px-2 py-2 border-b">Purchase Price</th>
                    <th className="px-2 py-2 border-b">Selling Price</th>
                    <th className="px-2 py-2 border-b">Variants Sold</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedInventory.map((record) => (
                    <tr key={record._id} className="hover:bg-gray-100">
                      <td className="px-4 py-2 border-b">{record?.variantName}</td>
                      <td className="px-4 py-2 border-b">{record.category_name}</td>
                      <td className="px-4 py-2 border-b">{record.subcategory_name}</td>
                      <td className="px-4 py-2 border-b">{record?.purchasePrice}</td>
                      <td className="px-4 py-2 border-b">{record?.sellingPrice}</td>
                      <td className="px-4 py-2 border-b">{record.totalSold}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                limit={limit}
                setLimit={setLimit}
                totalDocs={filteredInventory.length}
              />
            </div>
          </>
        ) : (
          <p className='text-2xl font-bold mb-4'>No Inventory Records Found.</p>
        )
      ) : (
        <Loader />
      )}
    </div>
  );
};

export default VariantSalesHistory;


//////// Before adding Export button code - 25-3-2026 ======
// import React, { useEffect, useState } from 'react'
// import axios from 'axios';
// import { baseURL } from "../../../../Services/api/baseURL";
// import Select from "react-select";
// import { useNavigate, useParams } from 'react-router';
// import Loader from '../../../Loader/Loader';
// import VariantSalesChart from './VariantSalesChart';
// import { ArrowLeftCircle } from 'react-feather';
// import Pagination from '../../../CustomPagination/Pagination';

// const VariantSalesHistory = () => {
//   const { id } = useParams();
//   const [inventory, setInventory] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [showChart, setShowChart] = useState(false);
//   const [page, setPage] = useState(1);
//   const [limit, setLimit] = useState(10);
//   const navigate = useNavigate()

//   const [filters, setFilters] = useState({
//     product: [],
//     category: '',
//     subcategory: '',
//     numberOfProducts: '',
//     start_date: "",
//     end_date: ""
//   });

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     const month = date.getMonth() + 1; // Months are zero-based
//     const day = date.getDate();
//     const year = date.getFullYear();
//     return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
//   };

//   const uniqueDates = () => {
//     const dates = inventory.map(record => formatDate(record.updatedAt));
//     return [...new Set(dates)].sort();
//   };

//   const uniqueValues = (key) => [...new Set(inventory.map(record => record[key]))].sort();

//   const getUniqueVariantNames = () => {
//     return [...new Set(inventory.map(record => record?.variantName))] // collect names
//       .filter(Boolean) // remove null/undefined
//       .sort(); // alphabetical order
//   };

//   const VariantMultiSelect = ({ filters, setFilters, getUniqueVariantNames }) => {
//     return (
//       <Select
//         isMulti
//         options={getUniqueVariantNames().map((name) => ({
//           value: name,
//           label: name,
//         }))}
//         value={filters.product.map((p) => ({ value: p, label: p }))}
//         onChange={(selected) =>
//           setFilters({
//             ...filters,
//             product: selected.map((s) => s.value),
//           })
//         }
//         placeholder="Select Variants"
//         className="w-full py-2 px-2 "
//         menuPortalTarget={document.body}  // 👈 renders dropdown outside container
//         styles={{
//           menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//           placeholder: (base) => ({
//             ...base,
//             marginLeft: "10px", // 👈 move placeholder text right
//             color: "#666",     // optional styling
//           }),
//           control: (base) => ({
//             ...base,
//             minHeight: "38px", // 👈 set height
//             height: "38px",    // 👈 fix height
//           }),
//           valueContainer: (base) => ({
//             ...base,
//             height: "38px",
//             // padding: "0 8px",
//           }),
//           input: (base) => ({
//             ...base,
//             margin: 0,
//             padding: 0,
//           }),
//         }}
//       />
//     );
//   };

//   const handleFilterChange = (e) => {
//     setFilters({
//       ...filters,
//       [e.target.name]: e.target.value
//     });
//     setPage(1)
//   };

//   const filteredInventory = inventory.filter(record => {

//     return (
//       (filters.product.length === 0 || filters.product.includes(record?.variantName)) &&
//       (filters.category === '' || record.category_name === filters.category) &&
//       (filters.subcategory === '' || record.subcategory_name === filters.subcategory) &&
//       (filters.numberOfProducts === '' || record.totalSold.toString() === filters.numberOfProducts)
//     );
//   });

//   const totalPages = Math.ceil(filteredInventory.length / limit);

//   const paginatedInventory = filteredInventory.slice(
//     (page - 1) * limit,
//     page * limit
//   );


//   const fetchData = async () => {
//     setLoading(true);
//     try {
//       const token = JSON.parse(localStorage.getItem("token"));
//       const params = {};
//       if (filters.start_date) params.start_date = filters.start_date;
//       if (filters.end_date) params.end_date = filters.end_date;

//       const response = await axios.get(
//         `${baseURL}/api/order/get-sold-variant-history/${id}`,
//         { headers: { Authorization: token }, params }
//       );
//       setInventory(response.data.data || []);
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };


//   useEffect(() => {
//     if (!id) return;
//     // initial fetch when id changes (no date filter)
//     fetchData();
//   }, [id]);

//   useEffect(() => {
//     if (filters.start_date && filters.end_date) {
//       fetchData();
//     }
//     if (!filters.start_date && !filters.end_date) {
//       // reset case: fetch all data
//       fetchData();
//     }
//   }, [filters.start_date, filters.end_date]);


//   const handleReset = () => {
//     setFilters(prev => ({ ...prev, start_date: "", end_date: "" }))
//     fetchData()
//   }
//   // Toggle chart visibility
//   const toggleChart = () => {
//     setShowChart(!showChart);
//   };

//   return (
//     <div className="p-4">
//       <button
//         onClick={() => navigate(-1)}
//         className=""
//       >
//         <ArrowLeftCircle className="text-[#d3178a]" />
//       </button>
//       <div className='flex justify-between '>
//         <h4 className='text-2xl font-bold mb-4'>Sold Variants Data for store </h4>
//         {!showChart && <div className="flex items-end gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium">Start Date</label>
//             <input
//               type="date"
//               name="start_date"
//               value={filters.start_date}
//               onChange={handleFilterChange}
//               className="border p-2 rounded"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium">End Date</label>
//             <input
//               type="date"
//               name="end_date"
//               value={filters.end_date}
//               onChange={handleFilterChange}
//               className="border p-2 rounded"
//             />
//           </div>
//           <button
//             onClick={handleReset}
//             className='rounded-2 bg-light-primary px-4 py-2.5 text-primary'
//           >
//             Reset
//           </button>
//           {/* New Chart Toggle Button */}
//         </div>}
//         <div className=' flex items-center'>
//           <button
//             onClick={toggleChart}
//             className={`px-4 py-2.5   rounded font-medium transition-colors ${showChart
//                 ? 'bg-red-500 text-white hover:bg-red-600'
//                 : 'bg-[#d3178a] text-white hover:bg-[#de379d]'
//               }`}
//           >
//             {showChart ? 'Hide Chart' : 'Sales Comparison Chart'}
//           </button>
//         </div>
//       </div>


//       {/* Conditional Chart Rendering */}
//       {showChart && (
//         <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
//           <div className="flex justify-between items-center mb-4">
//             <h5 className="text-lg font-semibold">Sales Comparison Chart</h5>
//             <button
//               onClick={() => setShowChart(false)}
//               className="text-gray-500 hover:text-gray-700 text-xl font-bold"
//             >
//               ×
//             </button>
//           </div>
//           <VariantSalesChart

//             storeId={id}
//           />
//         </div>
//       )}
//       {!loading ? (inventory.length > 0 ? (

//         <>
//           <div className="overflow-auto">
//             <table className="min-w-full bg-white border border-gray-200" >
//               <thead>
//                 <tr>

//                   <VariantMultiSelect
//                     filters={filters}
//                     setFilters={setFilters}
//                     getUniqueVariantNames={getUniqueVariantNames}
//                   />


//                   <th className="px-2 py-2 border-b">
//                     <select
//                       name="category"
//                       value={filters.category}
//                       onChange={handleFilterChange}
//                       className="w-full"
//                     >
//                       <option value="">Categories</option>
//                       {uniqueValues('category_name').map((value, index) => (
//                         <option key={index} value={value}>{value}</option>
//                       ))}
//                     </select>

//                   </th>
//                   <th className="px-2 py-2 border-b">
//                     <select
//                       name="subcategory"
//                       value={filters.subcategory}
//                       onChange={handleFilterChange}
//                       className="w-full"
//                     >
//                       <option value=""> Subcategories</option>
//                       {uniqueValues('subcategory_name').map((value, index) => (
//                         <option key={index} value={value}>{value}</option>
//                       ))}
//                     </select>

//                   </th>
//                   <th className="px-2 py-2 border-b">

//                     Purchase Price

//                   </th>
//                   <th className="px-2 py-2 border-b">

//                     Selling Price

//                   </th>
//                   <th className="px-2 py-2 border-b">

//                     Variants Sold

//                   </th>

//                 </tr>
//               </thead>
//               <tbody>
//                 {paginatedInventory.map((record) => (
//                   <tr key={record._id} className="hover:bg-gray-100">
//                     <td className="px-4 py-2 border-b">{record?.variantName}</td>
//                     <td className="px-4 py-2 border-b">{record.category_name}</td>
//                     <td className="px-4 py-2 border-b">{record.subcategory_name}</td>
//                     <td className="px-4 py-2 border-b">{record?.purchasePrice}</td>
//                     <td className="px-4 py-2 border-b">{record?.sellingPrice}</td>
//                     <td className="px-4 py-2 border-b">{record.totalSold}</td>

//                   </tr>
//                 ))}
//               </tbody>
//             </table>
//             <Pagination
//               page={page}
//               setPage={setPage}
//               totalPages={totalPages}
//               limit={limit}
//               setLimit={setLimit}
//               totalDocs={filteredInventory.length}
//             />

//           </div>
//         </>
//       ) : <p className='text-2xl font-bold mb-4'>No Inventory Records Found.</p>) : (
//         <Loader />
//       )}
//     </div>

//   );
// };


// export default VariantSalesHistory